// src/views/ImportQuestions.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionsAPI, plateformesAPI, formationsAPI, domainesExpertiseAPI, learndashSyncAPI } from '../api/api';

// Modale pour voir le détail d'une question
const QuestionPreviewModal = ({ question, onClose }) => {
  if (!question) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-bold text-gray-900">Détail de la question</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Intitulé</label>
              <p className="text-gray-900 bg-gray-50 p-3 rounded-md">{question.intitule}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de réponse</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded-md">{question.type_reponse}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded-md">{question.statut}</p>
              </div>
            </div>

            {question.feedback && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Feedback / Explication</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md">{question.feedback}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Réponses</label>
              <div className="space-y-2">
                {question.reponses && question.reponses.map((rep, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-md ${
                      rep.est_correct ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <span className="text-gray-900 flex-1">{rep.contenu}</span>
                    {rep.est_correct && (
                      <span className="text-green-600 text-sm font-medium">✓ Correcte</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function ImportQuestions() {
  const [file, setFile] = useState(null);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [previewVisible, setPreviewVisible] = useState(false);
  const [viewingQuestion, setViewingQuestion] = useState(null);

  // Configuration pour l'import
  const [formations, setFormations] = useState([]);
  const [domainesExpertise, setDomainesExpertise] = useState([]);
  const [plateformes, setPlateformes] = useState([]);

  const [selectedFormations, setSelectedFormations] = useState([]);
  const [selectedDomaines, setSelectedDomaines] = useState([]);
  const [selectedPlateformes, setSelectedPlateformes] = useState([]);
  const [autoSyncLearnDash, setAutoSyncLearnDash] = useState(true);

  // États de chargement et progression
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();

  // Charger les données nécessaires au montage
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [formationsData, domainesData, plateformesData] = await Promise.all([
        formationsAPI.getAll(),
        domainesExpertiseAPI.getAll(),
        plateformesAPI.getAll()
      ]);
      setFormations(formationsData);
      setDomainesExpertise(domainesData);
      setPlateformes(plateformesData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des formations, modules et plateformes');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError('');
      setMessage('');
      setParsedQuestions([]);
      setSelectedQuestions(new Set());
      setProgress(0);
    } else {
      setError('Veuillez sélectionner un fichier CSV valide');
    }
  };

  const downloadTemplate = () => {
    // Créer le contenu CSV avec en-têtes et exemples
    const csvContent = [
      'intitule,feedback,type_reponse,donnees_dynamiques,annee_reglementaire,reponse1,reponse1_correct,reponse2,reponse2_correct,reponse3,reponse3_correct,reponse4,reponse4_correct',
      '"Quelle est la capitale de la France ?","Paris est la capitale de la France depuis le IIIe siècle.","QCM","false","2024","Paris","1","Lyon","0","Marseille","0","Lille","0"',
      '"Le soleil est une étoile.","Le soleil est l\'étoile au centre de notre système solaire.","vrai/faux","false","","Vrai","1","Faux","0","","",""',
      '"Combien font 2 + 2 ?","2 + 2 font 4.","QCU","false","","3","0","4","1","5","0","6","0"',
      '"Quelle est la planète la plus proche du soleil ?","Mercure est la planète la plus proche du soleil.","QCM","true","2024","Mercure","1","Vénus","0","Terre","0","Mars","0"'
    ].join('\n');

    // Créer un blob et déclencher le téléchargement
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modele_questions_quiztech.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePreview = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier CSV');
      return;
    }

    setParsing(true);
    setError('');
    setMessage('');

    try {
      const csvData = await readFileAsText(file);
      const questions = parseCSV(csvData);

      if (questions.length === 0) {
        setError('Aucune question trouvée dans le fichier CSV');
        setParsing(false);
        return;
      }

      // Valider les questions
      const validatedQuestions = questions.map((q, idx) => ({
        ...q,
        _id: idx,
        _valid: validateQuestion(q),
        _errors: getQuestionErrors(q)
      }));

      setParsedQuestions(validatedQuestions);
      
      // Sélectionner par défaut les questions valides
      const validIndices = new Set(
        validatedQuestions
          .filter(q => q._valid)
          .map(q => q._id)
      );
      setSelectedQuestions(validIndices);

      setMessage(`${questions.length} questions trouvées - ${validIndices.size} valides`);
    } catch (err) {
      setError('Erreur lors de la lecture du fichier: ' + err.message);
    } finally {
      setParsing(false);
    }
  };

  const validateQuestion = (question) => {
    // Vérifier les champs obligatoires
    if (!question.intitule || question.intitule.trim() === '') return false;
    if (!question.type_reponse || !['QCM', 'QCU', 'vrai/faux'].includes(question.type_reponse)) return false;
    if (!question.reponses || question.reponses.length === 0) return false;
    
    // Vérifier qu'au moins une réponse est correcte
    const hasCorrectAnswer = question.reponses.some(r => r.est_correct);
    if (!hasCorrectAnswer) return false;

    return true;
  };

  const getQuestionErrors = (question) => {
    const errors = [];
    
    if (!question.intitule || question.intitule.trim() === '') {
      errors.push('Intitulé manquant');
    }
    if (!question.type_reponse || !['QCM', 'QCU', 'vrai/faux'].includes(question.type_reponse)) {
      errors.push('Type de réponse invalide');
    }
    if (!question.reponses || question.reponses.length === 0) {
      errors.push('Aucune réponse');
    } else {
      const hasCorrectAnswer = question.reponses.some(r => r.est_correct);
      if (!hasCorrectAnswer) {
        errors.push('Aucune réponse correcte');
      }
    }

    return errors;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const validIndices = new Set(
        parsedQuestions
          .filter(q => q._valid)
          .map(q => q._id)
      );
      setSelectedQuestions(validIndices);
    } else {
      setSelectedQuestions(new Set());
    }
  };

  const handleSelectQuestion = (id, checked) => {
    setSelectedQuestions(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleImport = async () => {
    if (selectedQuestions.size === 0) {
      setError('Veuillez sélectionner au moins une question à importer');
      return;
    }

    if (selectedFormations.length === 0) {
      setError('Veuillez sélectionner au moins une formation');
      return;
    }

    if (selectedDomaines.length === 0) {
      setError('Veuillez sélectionner au moins un module');
      return;
    }

    setImporting(true);
    setError('');
    setMessage('');
    setProgress(0);
    setImportedCount(0);
    setErrorCount(0);

    const questionsToImport = parsedQuestions.filter(q => selectedQuestions.has(q._id));
    const totalQuestions = questionsToImport.length;

    try {
      for (let i = 0; i < questionsToImport.length; i++) {
        const question = questionsToImport[i];
        
        try {
          const questionData = {
            intitule: question.intitule,
            feedback: question.feedback || '',
            type_reponse: question.type_reponse,
            statut: 'publié',
            donnees_dynamiques: question.donnees_dynamiques || false,
            annee_reglementaire: question.annee_reglementaire || null,
            formation_ids: selectedFormations,
            domaine_expertise_ids: selectedDomaines,
            plateformes: selectedPlateformes,
            auto_sync_learndash: autoSyncLearnDash,
            reponses: question.reponses
          };

          await questionsAPI.create(questionData);
          setImportedCount(prev => prev + 1);
        } catch (err) {
          console.error(`Erreur lors de l'importation de la question ${i + 1}:`, err);
          setErrorCount(prev => prev + 1);
        }

        setProgress(Math.round(((i + 1) / totalQuestions) * 100));
      }

      const successMessage = `${importedCount} questions importées avec succès`;
      const errorMessage = errorCount > 0 ? ` (${errorCount} échecs)` : '';
      setMessage(successMessage + errorMessage);

      // Redirection après succès
      setTimeout(() => {
        navigate('/questions');
      }, 2000);

    } catch (err) {
      setError('Erreur lors de l\'import: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  // Fonctions utilitaires
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Erreur lors de la lecture du fichier'));
      reader.readAsText(file);
    });
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const questions = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = parseCSVLine(line);
      if (values.length !== headers.length) {
        console.warn(`Ligne ${i} a un nombre incorrect de champs`, values, headers);
        continue;
      }

      const question = {};
      headers.forEach((header, index) => {
        question[header.toLowerCase()] = values[index]?.trim() || '';
      });

      const mappedQuestion = {
        intitule: question.intitule || question.question || question['intitulé'] || '',
        feedback: question.feedback || question.explication || '',
        type_reponse: question.type_reponse || question.type || 'QCM',
        statut: 'publié',
        donnees_dynamiques: question.donnees_dynamiques === '1' || question.donnees_dynamiques === 'true' || question.donnees_dynamiques === 'vrai',
        annee_reglementaire: question.annee_reglementaire || question.annee || null,
        reponses: []
      };

      // Gérer les réponses
      if (question.reponse1 || question.reponse2 || question.reponse3 || question.reponse4) {
        for (let j = 1; j <= 4; j++) {
          const reponseField = `reponse${j}`;
          const correctField = `reponse${j}_correct`;

          if (question[reponseField]) {
            mappedQuestion.reponses.push({
              contenu: question[reponseField],
              est_correct: question[correctField] === '1' || question[correctField] === 'true' || question[correctField] === 'vrai'
            });
          }
        }
      }

      questions.push(mappedQuestion);
    }

    return questions;
  };

  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current);
    return values;
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Importation de questions depuis CSV</h2>

          {/* Étape 1 : Upload du fichier */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Étape 1 : Sélectionner le fichier CSV
              </label>
              <button
                onClick={downloadTemplate}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Télécharger le modèle CSV
              </button>
            </div>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={parsing || importing}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
              <button
                onClick={handlePreview}
                disabled={!file || parsing || importing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {parsing ? 'Analyse...' : 'Prévisualiser'}
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Colonnes attendues : intitule, feedback, type_reponse, donnees_dynamiques, annee_reglementaire, reponse1, reponse1_correct, reponse2, reponse2_correct, ...
            </p>
          </div>

          {/* Étape 2 : Configuration et prévisualisation */}
          {parsedQuestions.length > 0 && (
            <>
              <div className="mb-6 border-t pt-6">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Étape 2 : Configuration de l'import
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Formations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Formations *
                    </label>
                    <div className="border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
                      {formations.map((formation) => (
                        <div key={formation.id} className="flex items-center mb-1">
                          <input
                            type="checkbox"
                            id={`formation-${formation.id}`}
                            checked={selectedFormations.includes(formation.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFormations([...selectedFormations, formation.id]);
                              } else {
                                setSelectedFormations(selectedFormations.filter(id => id !== formation.id));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor={`formation-${formation.id}`} className="ml-2 text-sm text-gray-700">
                            {formation.nom}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Modules */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modules *
                    </label>
                    <div className="border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
                      {domainesExpertise.map((domaine) => (
                        <div key={domaine.id} className="flex items-center mb-1">
                          <input
                            type="checkbox"
                            id={`domaine-${domaine.id}`}
                            checked={selectedDomaines.includes(domaine.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDomaines([...selectedDomaines, domaine.id]);
                              } else {
                                setSelectedDomaines(selectedDomaines.filter(id => id !== domaine.id));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor={`domaine-${domaine.id}`} className="ml-2 text-sm text-gray-700">
                            {domaine.titre}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Plateformes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plateformes à sync
                    </label>
                    <div className="border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
                      {plateformes.map((plateforme) => (
                        <div key={plateforme.id} className="flex items-center mb-1">
                          <input
                            type="checkbox"
                            id={`plateforme-${plateforme.id}`}
                            checked={selectedPlateformes.includes(plateforme.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPlateformes([...selectedPlateformes, plateforme.id]);
                              } else {
                                setSelectedPlateformes(selectedPlateformes.filter(id => id !== plateforme.id));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor={`plateforme-${plateforme.id}`} className="ml-2 text-sm text-gray-700">
                            {plateforme.nom}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Options
                    </label>
                    <div className="flex items-center mb-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoSyncLearnDash}
                          onChange={(e) => setAutoSyncLearnDash(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      </label>
                      <span className="ml-3 text-sm text-gray-700">Sync LearnDash auto</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tableau de prévisualisation */}
              <div className="mb-6 border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Étape 3 : Questions à importer ({selectedQuestions.size}/{parsedQuestions.length})
                  </h3>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="select-all"
                      checked={selectedQuestions.size === parsedQuestions.filter(q => q._valid).length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="select-all" className="text-sm text-gray-700">
                      Tout sélectionner (valides)
                    </label>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                            <input
                              type="checkbox"
                              checked={selectedQuestions.size === parsedQuestions.filter(q => q._valid).length}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Intitulé
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                            Rép.
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Statut
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parsedQuestions.map((question) => (
                          <tr
                            key={question._id}
                            className={`${
                              !question._valid ? 'bg-red-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedQuestions.has(question._id)}
                                onChange={(e) => handleSelectQuestion(question._id, e.target.checked)}
                                disabled={!question._valid}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 max-w-md truncate" title={question.intitule}>
                              {question.intitule}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                question.type_reponse === 'QCM' ? 'bg-blue-100 text-blue-800' :
                                question.type_reponse === 'QCU' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {question.type_reponse}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {question.reponses?.length || 0}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {question._valid ? (
                                <span className="text-green-600 flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Valide
                                </span>
                              ) : (
                                <span className="text-red-600 flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  Invalide
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => {
                                  setViewingQuestion(question);
                                  setPreviewVisible(true);
                                }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Voir
                              </button>
                              {!question._valid && question._errors.length > 0 && (
                                <div className="text-xs text-red-500 mt-1">
                                  {question._errors.join(', ')}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Progression et boutons d'action */}
              {(importing || selectedQuestions.size > 0) && (
                <div className="border-t pt-6">
                  {importing && (
                    <div className="mb-4">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Import en cours... {importedCount} importées, {errorCount} échecs
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setParsedQuestions([]);
                        setSelectedQuestions(new Set());
                        setFile(null);
                      }}
                      disabled={importing}
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={importing || selectedQuestions.size === 0}
                      className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {importing ? 'Import en cours...' : `Importer ${selectedQuestions.size} question(s)`}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Messages d'erreur et de succès */}
          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {message && (
            <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-md">
              {message}
            </div>
          )}
        </div>

        {/* Exemple de format CSV */}
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-medium text-gray-900">Format du fichier CSV</h3>
            <button
              onClick={downloadTemplate}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Télécharger le modèle
            </button>
          </div>
          <div className="bg-gray-50 p-4 rounded-md overflow-x-auto">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Colonnes requises :</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="text-xs">
                <span className="font-semibold text-gray-900">intitule</span>
                <p className="text-gray-500">Titre de la question</p>
              </div>
              <div className="text-xs">
                <span className="font-semibold text-gray-900">type_reponse</span>
                <p className="text-gray-500">QCM, QCU, ou vrai/faux</p>
              </div>
              <div className="text-xs">
                <span className="font-semibold text-gray-900">reponseX</span>
                <p className="text-gray-500">Contenu des réponses</p>
              </div>
              <div className="text-xs">
                <span className="font-semibold text-gray-900">reponseX_correct</span>
                <p className="text-gray-500">1 (correct) ou 0 (incorrect)</p>
              </div>
            </div>
            
            <h4 className="text-sm font-medium text-gray-700 mb-2">Colonnes optionnelles :</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="text-xs">
                <span className="font-semibold text-gray-900">feedback</span>
                <p className="text-gray-500">Explication</p>
              </div>
              <div className="text-xs">
                <span className="font-semibold text-gray-900">donnees_dynamiques</span>
                <p className="text-gray-500">true ou false</p>
              </div>
              <div className="text-xs">
                <span className="font-semibold text-gray-900">annee_reglementaire</span>
                <p className="text-gray-500">Année (ex: 2024)</p>
              </div>
            </div>

            <h4 className="text-sm font-medium text-gray-700 mb-2">Exemple de contenu :</h4>
            <p className="font-mono text-xs mb-2 text-gray-700 bg-white p-2 rounded border border-gray-200 overflow-x-auto">
              intitule,feedback,type_reponse,donnees_dynamiques,annee_reglementaire,reponse1,reponse1_correct,reponse2,reponse2_correct,reponse3,reponse3_correct
            </p>
            <p className="font-mono text-xs text-gray-600 bg-white p-2 rounded border border-gray-200 overflow-x-auto">
              "Quelle est la capitale de la France?","Paris est la capitale","QCM","false","2024","Paris","1","Lyon","0","Marseille","0"
            </p>
          </div>
        </div>
      </div>

      {/* Modale de prévisualisation d'une question */}
      {previewVisible && viewingQuestion && (
        <QuestionPreviewModal
          question={viewingQuestion}
          onClose={() => {
            setPreviewVisible(false);
            setViewingQuestion(null);
          }}
        />
      )}
    </div>
  );
}

export default ImportQuestions;
